import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faQuoteLeft,
    faStar,
    faChevronLeft,
    faChevronRight,
    faUserTie,
    faBalanceScale,
    faShieldAlt,
    faHandshake,
    faAward,
    faThumbsUp,
    faGraduationCap,
    faBuilding,
    faMapMarkerAlt,
    faIndustry,
    faBank,
    faHotel,
    faCar,
    faPlane,
    faSignal
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.15 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: "easeOut" }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const clientLogos = [
    { name: "Tata Projects Ltd", icon: faBuilding, category: "Infrastructure" },
    { name: "McNally Bharat Engineering", icon: faIndustry, category: "Engineering" },
    { name: "PhonePe", icon: faSignal, category: "Fintech" },
    { name: "Hero MotoCorp Ltd", icon: faCar, category: "Automobile" },
    { name: "Hindustan Construction Company", icon: faBuilding, category: "Construction" },
    { name: "Phooltas Transrail Ltd", icon: faIndustry, category: "Transport" },
    { name: "Naturals Dairy Pvt Ltd", icon: faIndustry, category: "FMCG" },
    { name: "Aishra Technofab Engineers", icon: faIndustry, category: "Engineering" },
    { name: "Energy United India Pvt Ltd", icon: faIndustry, category: "Energy" },
    { name: "HDFC Bank Ltd", icon: faBank, category: "Banking" },
    { name: "Juniper Hotels", icon: faHotel, category: "Hospitality" },
    { name: "GPCL Consulting Services", icon: faIndustry, category: "Consulting" },
    { name: "Omega Elevators", icon: faIndustry, category: "Manufacturing" },
    { name: "Adhunik Power", icon: faIndustry, category: "Power" },
    { name: "ATC Telecom", icon: faPlane, category: "Telecom" },
    { name: "Indus Towers Ltd", icon: faPlane, category: "Telecom" },
    { name: "Indigo", icon: faPlane, category: "Aviation" },
    { name: "Landmark Outdoor Media", icon: faBuilding, category: "Media" }
];

const testimonials = [
    {
        id: 1,
        name: "Mr. Rajesh Patel",
        position: "Legal Head",
        company: "Tata Projects Ltd",
        content: "RNA Law Chambers has been instrumental in handling our complex infrastructure project contracts. Their expertise in corporate and contract law ensured seamless execution of multiple high-value projects across India.",
        rating: 5,
        image: "👨‍💼",
        icon: faBuilding,
        category: "Corporate Law",
        location: "Mumbai",
        date: "2024",
        project: "₹500 Cr Infrastructure Deal"
    },
    {
        id: 2,
        name: "Ms. Ananya Sharma",
        position: "VP - Legal",
        company: "PhonePe",
        content: "In the fast-paced fintech industry, RNA's team provided agile legal solutions for our regulatory compliance and IP protection needs. Their understanding of digital finance laws is exceptional.",
        rating: 5,
        image: "👩‍💼",
        icon: faSignal,
        category: "Fintech Law",
        location: "Bangalore",
        date: "2023",
        project: "Digital Payments Compliance"
    },
    {
        id: 3,
        name: "Mr. Vikram Malhotra",
        position: "Chief Legal Officer",
        company: "Hero MotoCorp Ltd",
        content: "RNA Law Chambers handled our international manufacturing agreements with precision. Their cross-border legal expertise helped us expand operations across 3 new countries without legal hurdles.",
        rating: 5,
        image: "👨‍🔧",
        icon: faCar,
        category: "International Law",
        location: "Delhi NCR",
        date: "2024",
        project: "Global Expansion Strategy"
    },
    {
        id: 4,
        name: "Mrs. Priya Nair",
        position: "Legal Director",
        company: "HDFC Bank Ltd",
        content: "For complex banking regulations and mergers, RNA's team demonstrated unparalleled expertise. Their attention to detail in financial compliance matters is remarkable.",
        rating: 5,
        image: "👩‍💼",
        icon: faBank,
        category: "Banking Law",
        location: "Mumbai",
        date: "2023",
        project: "Regulatory Compliance Audit"
    },
    {
        id: 5,
        name: "Mr. Arjun Singh",
        position: "Head of Legal",
        company: "Indus Towers Ltd",
        content: "RNA Law Chambers resolved our telecom infrastructure disputes efficiently. Their strategic approach saved us from prolonged litigation and secured favorable settlements.",
        rating: 5,
        image: "👨‍💼",
        icon: faPlane,
        category: "Telecom Law",
        location: "Delhi",
        date: "2024",
        project: "Tower Infrastructure Rights"
    },
    {
        id: 6,
        name: "Ms. Neha Kapoor",
        position: "Legal Counsel",
        company: "Indigo Airlines",
        content: "Navigating aviation laws requires specialized knowledge. RNA's team provided excellent support in our aircraft leasing agreements and international route permissions.",
        rating: 5,
        image: "👩‍✈️",
        icon: faPlane,
        category: "Aviation Law",
        location: "Gurugram",
        date: "2023",
        project: "Fleet Expansion Contracts"
    }
];

const practiceAreas = [
    { icon: faBalanceScale, name: "Corporate Law", clients: ["Tata", "PhonePe", "Hero"] },
    { icon: faBuilding, name: "Infrastructure Law", clients: ["HCC", "McNally"] },
    { icon: faBank, name: "Banking & Finance", clients: ["HDFC", "PhonePe"] },
    { icon: faPlane, name: "Telecom Law", clients: ["ATC", "Indus"] },
    { icon: faIndustry, name: "Manufacturing", clients: ["Omega", "Aishra"] },
    { icon: faPlane, name: "Aviation Law", clients: ["Indigo", "Juniper"] }
];

const stats = [
    { label: "Fortune 500 Clients", value: "18+", suffix: "Major Corporations" },
    { label: "Case Success Rate", value: "96%", suffix: "In Favor of Clients" },
    { label: "Years Serving", value: "15+", suffix: "Corporate Sector" },
    { label: "Sector Expertise", value: "12", suffix: "Industries Covered" }
];

const StarRating = ({ rating }) => {
    return (
        <div className="d-flex gap-1">
            {[...Array(5)].map((_, i) => (
                <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    style={{
                        color: i < rating ? "var(--gold)" : "var(--gray-light)",
                        fontSize: "0.9rem"
                    }}
                />
            ))}
        </div>
    );
};

export const Testimonials = () => {
    const [activeFilter, setActiveFilter] = useState("All");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [expandedTestimonial, setExpandedTestimonial] = useState(null);

    const categories = ["All", ...new Set(testimonials.map(t => t.category))];

    const filteredTestimonials = activeFilter === "All"
        ? testimonials
        : testimonials.filter(t => t.category === activeFilter);

    const featuredTestimonial = filteredTestimonials[currentIndex];

    const nextTestimonial = () => {
        setCurrentIndex((prev) =>
            prev === filteredTestimonials.length - 1 ? 0 : prev + 1
        );
    };

    const prevTestimonial = () => {
        setCurrentIndex((prev) =>
            prev === 0 ? filteredTestimonials.length - 1 : prev - 1
        );
    };

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span style={{
                            color: "var(--gold)",
                            fontWeight: "600",
                            letterSpacing: "2px",
                            fontSize: "0.9rem",
                            textTransform: "uppercase"
                        }}>
                            Client Testimonials
                        </span>
                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Trusted by India's Leading Corporations.
                        </motion.h4>
                    </motion.div>
                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        For over 15 years, RNA Law Chambers has been the preferred legal partner for
                        Fortune 500 companies and industry leaders across multiple sectors.
                    </motion.p>

                </motion.div>

                {/* Clients Marquee */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-4">
                        <h3 className="fw-semibold mb-3" style={{ color: "var(--primary-dark)" }}>
                            Some of Our Prominent Clients
                        </h3>
                        <div className="small" style={{ color: "var(--gray-medium)" }}>
                            Trusted by industry leaders across sectors
                        </div>
                    </div>

                    <motion.div
                        className="d-flex flex-wrap justify-content-center gap-3 gap-md-4 mb-5"
                        variants={itemVariants}
                    >
                        {clientLogos.map((client, index) => (
                            <motion.div
                                key={index}
                                className="d-flex flex-column align-items-center p-3 rounded-3"
                                style={{
                                    backgroundColor: "var(--white)",
                                    border: "1px solid var(--gold-light)",
                                    minWidth: "180px",
                                    transition: "var(--transition)"
                                }}
                                whileHover={{
                                    y: -5,
                                    borderColor: "var(--gold)",
                                    boxShadow: "var(--shadow-medium)"
                                }}
                            >
                                <div className="mb-2"
                                    style={{
                                        color: "var(--gold)",
                                        fontSize: "1.5rem"
                                    }}
                                >
                                    <FontAwesomeIcon icon={client.icon} />
                                </div>
                                <div className="text-center">
                                    <div className="fw-semibold" style={{
                                        color: "var(--primary-dark)",
                                        fontSize: "0.9rem",
                                        lineHeight: 1.3
                                    }}>
                                        {client.name.split(' ')[0]}
                                    </div>
                                    <div className="small" style={{
                                        color: "var(--gray-medium)",
                                        fontSize: "0.75rem",
                                        marginTop: "2px"
                                    }}>
                                        {client.category}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>

                {/* Stats Section */}
                <motion.div
                    className="px-3 mb-5"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    {/* Stats Section - More Compact */}
                    <motion.div variants={itemVariants} className="mb-4">
                        <h3 className="fw-bold mb-4 text-center"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            Our Track Record
                        </h3>

                        <div className="row g-3">
                            {stats.map((stat, index) => (
                                <motion.div key={index} className="col-6 col-md-3" variants={itemVariants}>
                                    <motion.div
                                        className="text-center p-3 rounded-4 h-100 d-flex flex-column justify-content-center"
                                        style={{
                                            backgroundColor: "var(--white)",
                                            border: "1px solid var(--gold-light)",
                                            boxShadow: "var(--shadow-soft)",
                                            transition: "var(--transition)",
                                            minHeight: "120px"
                                        }}
                                        whileHover={{
                                            y: -5,
                                            boxShadow: "var(--shadow-medium)",
                                            borderColor: "var(--gold)",
                                            backgroundColor: "rgba(184, 134, 11, 0.02)"
                                        }}
                                    >
                                        <div className="display-6 fw-bold mb-1"
                                            style={{
                                                color: "var(--gold)",
                                                fontFamily: "'Playfair Display', serif",
                                                lineHeight: 1.2
                                            }}
                                        >
                                            {stat.value}
                                        </div>
                                        <div className="fw-semibold mb-1" style={{
                                            color: "var(--primary-dark)",
                                            fontSize: "0.9rem"
                                        }}>
                                            {stat.label}
                                        </div>
                                        <div className="small" style={{
                                            color: "var(--gray-medium)",
                                            fontSize: "0.75rem"
                                        }}>
                                            {stat.suffix}
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Featured Testimonial Section */}
                    <motion.div variants={itemVariants} className="mb-5">
                        <div className="row g-4 align-items-stretch">
                            {/* Featured Testimonial Card */}
                            <div className="col-lg-8">
                                <motion.div
                                    className="h-100 rounded-4 position-relative overflow-hidden"
                                    variants={itemVariants}
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-medium)",
                                        minHeight: "380px"
                                    }}
                                    whileHover={{ boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                                >
                                    {/* Decorative background */}
                                    <div className="position-absolute top-0 end-0 w-100 h-100"
                                        style={{
                                            background: "linear-gradient(135deg, transparent 60%, rgba(184, 134, 11, 0.03) 100%)",
                                            zIndex: 0
                                        }}
                                    ></div>

                                    <div className="position-relative z-1 p-4 p-md-5 h-100 d-flex flex-column">
                                        {/* Quote Icon */}
                                        <FontAwesomeIcon
                                            icon={faQuoteLeft}
                                            className="mb-4"
                                            style={{
                                                fontSize: "2.5rem",
                                                color: "var(--gold-light)",
                                                opacity: 0.2
                                            }}
                                        />

                                        {/* Testimonial Content */}
                                        <div className="flex-grow-1">
                                            <p className="fs-5 mb-4" style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.8,
                                                fontStyle: "italic",
                                                minHeight: "120px"
                                            }}>
                                                "{featuredTestimonial.content}"
                                            </p>

                                            <div className="mt-auto">
                                                {/* Client Info */}
                                                <div className="d-flex align-items-center gap-3 mb-3">
                                                    <div className="display-4">{featuredTestimonial.image}</div>
                                                    <div>
                                                        <h5 className="fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                            {featuredTestimonial.name}
                                                        </h5>
                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                            <div style={{ color: "var(--gold)", fontWeight: "500", fontSize: "0.9rem" }}>
                                                                {featuredTestimonial.position}
                                                            </div>
                                                            <div style={{ color: "var(--gray-medium)" }}>•</div>
                                                            <div style={{
                                                                color: "var(--primary-blue)",
                                                                fontWeight: "600",
                                                                fontSize: "0.9rem"
                                                            }}>
                                                                {featuredTestimonial.company}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Tags and Navigation */}
                                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 pt-3 border-top"
                                                    style={{ borderColor: "var(--gold-light)" }}
                                                >
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        <div className="badge px-3 py-2"
                                                            style={{
                                                                backgroundColor: "rgba(184, 134, 11, 0.08)",
                                                                color: "var(--gold)",
                                                                border: "1px solid var(--gold-light)",
                                                                fontSize: "0.8rem"
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={featuredTestimonial.icon} className="me-2" />
                                                            {featuredTestimonial.category}
                                                        </div>
                                                        <div className="badge px-3 py-2"
                                                            style={{
                                                                backgroundColor: "rgba(44, 62, 80, 0.08)",
                                                                color: "var(--primary-blue)",
                                                                border: "1px solid var(--primary-blue)",
                                                                fontSize: "0.8rem"
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                                                            {featuredTestimonial.location}
                                                        </div>
                                                    </div>

                                                    <div className="d-flex gap-2">
                                                        <motion.button
                                                            className="btn p-2 rounded-circle d-flex align-items-center justify-content-center"
                                                            onClick={prevTestimonial}
                                                            style={{
                                                                backgroundColor: "var(--white)",
                                                                border: "1px solid var(--gold-light)",
                                                                color: "var(--gold)",
                                                                width: "40px",
                                                                height: "40px"
                                                            }}
                                                            whileHover={{
                                                                backgroundColor: "var(--gold)",
                                                                color: "var(--white)"
                                                            }}
                                                            whileTap={{ scale: 0.95 }}
                                                        >
                                                            <FontAwesomeIcon icon={faChevronLeft} />
                                                        </motion.button>
                                                        <motion.button
                                                            className="btn p-2 rounded-circle d-flex align-items-center justify-content-center"
                                                            onClick={nextTestimonial}
                                                            style={{
                                                                backgroundColor: "var(--gold)",
                                                                border: "1px solid var(--gold)",
                                                                color: "var(--white)",
                                                                width: "40px",
                                                                height: "40px"
                                                            }}
                                                            whileHover={{
                                                                backgroundColor: "var(--gold-light)",
                                                                borderColor: "var(--gold-light)"
                                                            }}
                                                            whileTap={{ scale: 0.95 }}
                                                        >
                                                            <FontAwesomeIcon icon={faChevronRight} />
                                                        </motion.button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>

                            {/* Industry Expertise Sidebar */}
                            <div className="col-lg-4">
                                <motion.div variants={itemVariants} className="h-100">
                                    <div className="p-4 rounded-4 h-100"
                                        style={{
                                            backgroundColor: "var(--white)",
                                            border: "1px solid var(--gold-light)",
                                            boxShadow: "var(--shadow-soft)"
                                        }}
                                    >
                                        <h4 className="fw-bold mb-4 d-flex align-items-center gap-2"
                                            style={{
                                                fontFamily: "'Playfair Display', serif",
                                                color: "var(--primary-dark)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faAward} style={{ color: "var(--gold)" }} />
                                            Industry Expertise
                                        </h4>

                                        <div className="vstack gap-3">
                                            {practiceAreas.map((area, index) => (
                                                <motion.div
                                                    key={index}
                                                    className="d-flex align-items-center p-3 rounded-3 position-relative overflow-hidden"
                                                    style={{
                                                        backgroundColor: "rgba(184, 134, 11, 0.03)",
                                                        border: "1px solid transparent",
                                                        transition: "var(--transition)",
                                                        cursor: "pointer"
                                                    }}
                                                    whileHover={{
                                                        backgroundColor: "rgba(184, 134, 11, 0.08)",
                                                        borderColor: "var(--gold-light)"
                                                    }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    {/* Background effect on hover */}
                                                    <div className="position-absolute top-0 start-0 w-100 h-100"
                                                        style={{
                                                            background: "linear-gradient(90deg, transparent 95%, var(--gold) 100%)",
                                                            opacity: 0,
                                                            transition: "opacity 0.3s ease"
                                                        }}
                                                    ></div>

                                                    <div className="position-relative z-1 d-flex align-items-center gap-3 w-100">
                                                        <div className="p-2 rounded-3 d-flex align-items-center justify-content-center"
                                                            style={{
                                                                backgroundColor: "rgba(184, 134, 11, 0.1)",
                                                                color: "var(--gold)",
                                                                width: "48px",
                                                                height: "48px",
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={area.icon} style={{ fontSize: "1.1rem" }} />
                                                        </div>

                                                        <div className="flex-grow-1">
                                                            <div className="fw-semibold d-flex justify-content-between align-items-center">
                                                                <span style={{ color: "var(--primary-dark)" }}>
                                                                    {area.name}
                                                                </span>
                                                                <span className="badge"
                                                                    style={{
                                                                        backgroundColor: "var(--gold)",
                                                                        color: "var(--white)",
                                                                        fontSize: "0.7rem",
                                                                        padding: "2px 8px"
                                                                    }}
                                                                >
                                                                    {area.clients.length}
                                                                </span>
                                                            </div>
                                                            <div className="small mt-1" style={{
                                                                color: "var(--gray-medium)",
                                                                fontSize: "0.8rem"
                                                            }}>
                                                                {area.clients.join(", ")}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Filter & Testimonials Grid */}
                    <motion.div variants={itemVariants}>
                        {/* Filter Buttons */}
                        <div className="text-center mb-4">
                            <h4 className="fw-bold mb-3"
                                style={{
                                    fontFamily: "'Playfair Display', serif",
                                    color: "var(--primary-dark)"
                                }}
                            >
                                Client Stories by Category
                            </h4>
                            <p className="mb-4" style={{ color: "var(--gray-medium)", maxWidth: "600px", margin: "0 auto" }}>
                                Explore testimonials from clients across different legal practice areas
                            </p>
                        </div>

                        <div className="d-flex justify-content-center flex-wrap gap-2 mb-4">
                            {categories.map((category) => (
                                <motion.button
                                    key={category}
                                    className={`btn px-4 py-2 rounded-pill ${activeFilter === category ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveFilter(category);
                                        setCurrentIndex(0);
                                    }}
                                    variants={itemVariants}
                                    style={{
                                        backgroundColor: activeFilter === category ? "var(--gold)" : "transparent",
                                        color: activeFilter === category ? "var(--white)" : "var(--primary-dark)",
                                        border: `2px solid ${activeFilter === category ? "var(--gold)" : "var(--gold-light)"}`,
                                        fontWeight: "500",
                                        transition: "var(--transition)",
                                        fontSize: "0.9rem"
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        backgroundColor: activeFilter === category ? "var(--gold-light)" : "rgba(184, 134, 11, 0.08)"
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {category}
                                    {activeFilter === category && (
                                        <span className="ms-2">✓</span>
                                    )}
                                </motion.button>
                            ))}
                        </div>

                        {/* Testimonials Grid */}
                        <div className="row g-4">
                            {filteredTestimonials.map((testimonial, index) => (
                                <motion.div
                                    key={testimonial.id}
                                    className="col-md-6 col-lg-4"
                                    variants={itemVariants}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <motion.div
                                        className="h-100 rounded-4 d-flex flex-column position-relative overflow-hidden"
                                        style={{
                                            backgroundColor: "var(--white)",
                                            border: "1px solid var(--gold-light)",
                                            boxShadow: "var(--shadow-soft)",
                                            transition: "var(--transition)"
                                        }}
                                        whileHover={{
                                            y: -8,
                                            boxShadow: "var(--shadow-medium)",
                                            borderColor: "var(--gold)"
                                        }}
                                        onClick={() => setExpandedTestimonial(
                                            expandedTestimonial === testimonial.id ? null : testimonial.id
                                        )}
                                    >
                                        {/* Top accent */}
                                        <div className="position-absolute top-0 start-0 w-100"
                                            style={{
                                                height: "4px",
                                                background: "linear-gradient(90deg, var(--gold) 0%, var(--gold-light) 100%)",
                                                opacity: 0.8
                                            }}
                                        ></div>

                                        <div className="p-4 flex-grow-1 d-flex flex-column">
                                            {/* Content */}
                                            <div className="mb-4 flex-grow-1">
                                                <p className="mb-3" style={{
                                                    color: "var(--text-primary)",
                                                    lineHeight: 1.6,
                                                    fontSize: "0.9rem",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: expandedTestimonial === testimonial.id ? "unset" : 3,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    minHeight: "72px"
                                                }}>
                                                    "{testimonial.content}"
                                                </p>

                                                <StarRating rating={testimonial.rating} />
                                            </div>

                                            {/* Client Info */}
                                            <div className="d-flex align-items-center gap-3 pt-3 border-top"
                                                style={{ borderColor: "var(--gray-light)" }}
                                            >
                                                <div className="display-5 flex-shrink-0">{testimonial.image}</div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-bold mb-1" style={{
                                                        color: "var(--primary-dark)",
                                                        fontSize: "0.95rem"
                                                    }}>
                                                        {testimonial.name}
                                                    </div>
                                                    <div className="small d-flex align-items-center gap-1 flex-wrap">
                                                        <span style={{ color: "var(--primary-blue)", fontWeight: "600" }}>
                                                            {testimonial.company}
                                                        </span>
                                                        <span style={{ color: "var(--gray-medium)" }}>•</span>
                                                        <span style={{ color: "var(--gold)" }}>
                                                            {testimonial.category}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expand/Collapse Button */}
                                        <div className="p-3 border-top"
                                            style={{
                                                borderColor: "var(--gold-light)",
                                                backgroundColor: "rgba(184, 134, 11, 0.02)"
                                            }}
                                        >
                                            <button
                                                className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                                                style={{
                                                    color: "var(--gold)",
                                                    fontSize: "0.85rem",
                                                    fontWeight: "500",
                                                    background: "none",
                                                    border: "none",
                                                    padding: "0"
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedTestimonial(
                                                        expandedTestimonial === testimonial.id ? null : testimonial.id
                                                    );
                                                }}
                                            >
                                                {expandedTestimonial === testimonial.id ? "Show Less" : "Read Full Story"}
                                                <FontAwesomeIcon
                                                    icon={expandedTestimonial === testimonial.id ? faChevronLeft : faChevronRight}
                                                    style={{ fontSize: "0.8rem" }}
                                                />
                                            </button>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>

                {/* CTA Section */}
         

                        
                      
                <Cta Title="Join India's Leading Corporations" description="Trusted by 18+ Fortune 500 companies. Let RNA Law Chambers provide the legal excellence your corporation deserves."/>
            </div>
        </div>
    );
};